// firebase.service.ts - 共通 Firestore + Auth 操作封装（使用 Firebase Modular API）
import { inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  getDocs,
  deleteDoc,
  runTransaction,
  query,
  where,
  Timestamp,
  orderBy,
} from '@angular/fire/firestore';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  updateProfile,
  EmailAuthProvider,
  User,
} from '@angular/fire/auth';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  Storage
} from '@angular/fire/storage';
import { BehaviorSubject } from 'rxjs';
import { FirebaseApp } from '@angular/fire/app';
import { mergeReceiptUrls } from './receipt-urls';
import { UserData, UserPermissions } from '../types/user.interface';
import { CategorySummary, DaySummary } from '../types/data-type.interface';
import { getIdTokenResult, reauthenticateWithCredential } from 'firebase/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { HelperService } from './helper.service';

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  currentUser$ = new BehaviorSubject<any>(undefined);
  currentUserData: any = null;
  private storage = getStorage(inject(FirebaseApp));
  private companyId: string = 'webspark';
  
  mustChangePassword: boolean = false;

  constructor(private firestore: Firestore,
    private auth: Auth,
    private hpService: HelperService,
    private functions: Functions,
  ) {
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        if (!user.email) {
          // 没有 email（某些 auth provider 可能没有）：视为未通过认证，方向收紧
          this.currentUserData = null;
          this.currentUser$.next(null);
          return;
        }
        try {
          this.setCompanyId(this.convertCompany(user.email));
          const userDoc = await getDoc(doc(this.firestore, `companies/${this.companyId}/users/${user.uid}`));
          if (!userDoc.data()) {
            // 有 Auth 账号但无公司用户文档：视为未通过认证，与 login() 的 "Account not found!" 口径一致
            this.currentUserData = null;
            this.currentUser$.next(null);
            return;
          }
          this.currentUserData = userDoc.data();
          this.mustChangePassword = this.currentUserData?.mustChangePassword ?? false;
          this.currentUser$.next({ ...user, ...this.currentUserData });
        } catch {
          // getDoc 抛异常（如网络问题）：按未认证处理，避免 currentUser$ 永不 emit 导致守卫永久等待
          this.currentUserData = null;
          this.currentUser$.next(null);
        }
      } else {
        this.currentUserData = null;
        this.currentUser$.next(null);
      }
    });
  }

  private convertCompany(email: string): string {
    if (email.endsWith('local')) {
      const domainPart = email.split("@")[1];
      const result = domainPart.split(".")[0];
      return result;
    } else if (email.endsWith('gmail.com')) {
      return 'webspark';
    } else {
      return 'default';
    }
  }

  async login(email: string, password: string) {
    const result = await signInWithEmailAndPassword(this.auth, email, password);
    const targetCompany =  this.convertCompany(email);

    const setClaims = httpsCallable(this.functions, 'setCustomClaims');
    await setClaims({ companyId: targetCompany, uid: result.user.uid });

    const idTokenResult = await getIdTokenResult(result.user, true); // ✅ true 表示强制刷新

    const docSnap = await getDoc(doc(this.firestore, `companies/${targetCompany}/summary/balance`));
    this.setCompanyId(targetCompany);

    if (!docSnap.exists()) {
      await this.initNewDoc(result.user);
    }

    const userKey = `companies/${this.companyId}/users/${result.user.uid}`;
    const userDoc = await getDoc(doc(this.firestore, userKey));

    if (!userDoc.data()) {
      throw new Error("Account not found!");     
    }
    await updateDoc(doc(this.firestore, userKey), {
      lastLoginAt: new Date()
    });
    this.currentUserData = userDoc.data();
    this.mustChangePassword = this.currentUserData.mustChangePassword;

    return { user: result.user, data: this.currentUserData };
  }

  async initNewDoc(user: User) {
    // 设置初期管理员
    const displayName = user.email.split("@")[0];
    const userDoc = {
      id: user.uid,
      email: user.email,
      displayName: displayName,
      permissions: { isAdmin: true, canEntry: true, canApprove: true},
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const userRef = doc(this.firestore, `companies/${this.companyId}/users/${user.uid}`);
    await setDoc(userRef, userDoc);

    // 更新显示名
    await updateProfile(user, {
      displayName: displayName,
      photoURL: null // 可选：设置头像 URL
    });

    // 设置余额初期值
    const balanceDoc = {
      total: 1000_000
    }
    const balaceRef = doc(this.firestore, `companies/${this.companyId}/summary/balance`);
    await setDoc(balaceRef, balanceDoc);
  }

  getLoginName(): string {
    return this.auth.currentUser?.displayName
  }
  
  getPermissions(): UserPermissions {
    const noPermissions: UserPermissions = { isAdmin: false, canEntry: false, canApprove: false };
    return (this.currentUserData?.permissions as UserPermissions) ?? noPermissions;
  }

  logout() {
    return signOut(this.auth);
  }

  async changePassword(oldPassword: string, newPassword: string) {
    if (!this.auth.currentUser) throw new Error('未登录');
     // 1️⃣ 生成旧密码的凭据
    const credential = EmailAuthProvider.credential(this.auth.currentUser.email, oldPassword);

    try {
      // 2️⃣ 重新认证，确保是本人操作
      await reauthenticateWithCredential(this.auth.currentUser, credential);
  
      // 3️⃣ 更新为新密码
      await updatePassword(this.auth.currentUser, newPassword);
      this.mustChangePassword = false;

      const userKey = `companies/${this.companyId}/users/${this.auth.currentUser.uid}`;
      await updateDoc(doc(this.firestore, userKey), {
        mustChangePassword: false,
        updatedAt: new Date()
      });

    } catch (error: any) {
      // 错误处理
      if (error.code === 'auth/wrong-password'
        || error.code === 'auth/invalid-login-credentials'
      ) {
        throw new Error('旧密码错误');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('新密码太弱');
      } else {
        console.error(error.message);
        throw new Error('密码更新失败');
      }
    }
  }

  async addEntry(data: any) {
    const uid = this.auth.currentUser?.displayName;
    if (!uid) throw new Error('未登录');
    data.receipt = null;
    const entry = {
      ...data,
      createdBy: uid,
      createdAt: new Date(),
      status: 'pending',
      submitFlg: false,
      refundFlg: false,
      excludedFlg: false,
    };
    return addDoc(collection(this.firestore, `companies/${this.companyId}/entries`), entry);
  }

  async approveEntry(entryId: string) {
    const entryRef = doc(this.firestore, `companies/${this.companyId}/entries/${entryId}`);
    const balanceRef = doc(this.firestore, `companies/${this.companyId}/summary/balance`);
    const uid = this.auth.currentUser?.displayName;
    let balanceHistory: any;
    await runTransaction(this.firestore, async (tx) => {
      const entrySnap = await tx.get(entryRef);
      if (!entrySnap.exists()) throw new Error('无效账目');
      const entryData = entrySnap.data();
      if (entryData.status !== 'pending') throw new Error('无效账目');

      const balanceSnap = await tx.get(balanceRef);
      const amount = entryData.amount;
      const type = entryData.type;
      const adjustAmt = type === 'income' ? amount : -amount;
      const beforeBal = balanceSnap.exists() ? balanceSnap.data().total : 0;

      tx.update(entryRef, {
        status: 'approved',
        approvedBy: uid,
        approvedAt: new Date(),
      });
      tx.set(balanceRef, { total: beforeBal + adjustAmt });

      balanceHistory = {
        type,
        amount,
        beforeBal,
        afterBal: beforeBal + adjustAmt,
        entryId,
        entryTitle: entryData.details,
        createdBy: entryData.createdBy,
        createdAt: new Date(),
      };
    });
    await addDoc(collection(this.firestore, `companies/${this.companyId}/summary/balance/history`), balanceHistory);
  }


  async submitEntry(entryId: string) {
    const key = `companies/${this.companyId}/entries/${entryId}`;
    const entrySnap = await getDoc(doc(this.firestore, key));

    if (!entrySnap.exists() || entrySnap.data().status != 'approved') {
      throw new Error('提交失败!');
    }
    if (entrySnap.data().excludedFlg) {
      throw new Error('除外记录不能提交');
    }
    await updateDoc(doc(this.firestore, `companies/${this.companyId}/entries`, entryId), {
      submitFlg: true,
      updatedAt: new Date()
    });
  }

  async excludeEntry(entryId: string) {
    const entryRef = doc(this.firestore, `companies/${this.companyId}/entries/${entryId}`);
    const balanceRef = doc(this.firestore, `companies/${this.companyId}/summary/balance`);
    const uid = this.auth.currentUser?.displayName;
    let balanceHistory: any;

    await runTransaction(this.firestore, async (tx) => {
      const entrySnap = await tx.get(entryRef);
      if (!entrySnap.exists()) throw new Error('撤销失败! 只能撤销已承认的记录');
      const entryData = entrySnap.data();
      if (entryData.status != 'approved') {
        throw new Error('撤销失败! 只能撤销已承认的记录');
      }
      if (entryData.submitFlg) {
        throw new Error('撤销失败! 已提交的记录不能撤销');
      }
      if (entryData.excludedFlg) {
        throw new Error('该记录已是撤销状态');
      }

      const balanceSnap = await tx.get(balanceRef);
      const amount = Number(entryData.amount) || 0;
      const adjustAmt = entryData.type === 'income' ? -amount : amount;
      const beforeBal = balanceSnap.exists() ? balanceSnap.data().total : 0;
      const afterBal = beforeBal + adjustAmt;

      tx.update(entryRef, {
        excludedFlg: true,
        excludedBy: uid,
        excludedAt: new Date(),
        updatedAt: new Date(),
      });
      tx.set(balanceRef, { total: afterBal });

      balanceHistory = {
        operation: '撤销',
        type: entryData.type,
        amount: adjustAmt,
        beforeBal,
        afterBal,
        entryId,
        entryTitle: `撤销：${entryData.details}`,
        originalEntryAmount: amount,
        createdBy: uid,
        createdAt: new Date(),
      };
    });

    await addDoc(collection(this.firestore, `companies/${this.companyId}/summary/balance/history`), balanceHistory);
  }

  async unexcludeEntry(entryId: string) {
    const entryRef = doc(this.firestore, `companies/${this.companyId}/entries/${entryId}`);
    const balanceRef = doc(this.firestore, `companies/${this.companyId}/summary/balance`);
    const uid = this.auth.currentUser?.displayName;
    let balanceHistory: any;

    await runTransaction(this.firestore, async (tx) => {
      const entrySnap = await tx.get(entryRef);
      if (!entrySnap.exists()) throw new Error('恢复失败!');
      const entryData = entrySnap.data();
      if (entryData.status != 'approved') {
        throw new Error('恢复失败!');
      }
      if (!entryData.excludedFlg) {
        throw new Error('该记录不是撤销状态');
      }

      const balanceSnap = await tx.get(balanceRef);
      const amount = Number(entryData.amount) || 0;
      const adjustAmt = entryData.type === 'income' ? amount : -amount;
      const beforeBal = balanceSnap.exists() ? balanceSnap.data().total : 0;
      const afterBal = beforeBal + adjustAmt;

      tx.update(entryRef, {
        excludedFlg: false,
        excludedBy: null,
        excludedAt: null,
        updatedAt: new Date(),
      });
      tx.set(balanceRef, { total: afterBal });

      balanceHistory = {
        operation: '恢复',
        type: entryData.type,
        amount: adjustAmt,
        beforeBal,
        afterBal,
        entryId,
        entryTitle: `恢复：${entryData.details}`,
        originalEntryAmount: amount,
        createdBy: uid,
        createdAt: new Date(),
      };
    });

    await addDoc(collection(this.firestore, `companies/${this.companyId}/summary/balance/history`), balanceHistory);
  }

  async refundEntry(entryId: string) {
    const key = `companies/${this.companyId}/entries/${entryId}`;
    const entrySnap = await getDoc(doc(this.firestore, key));
    if (!entrySnap.exists()) throw new Error('退款失败!');
    const entryData = entrySnap.data();
    if (entryData.status != 'approved') {
      throw new Error('退款失败!');
    }
    if (!entryData.isFund) {
      throw new Error('只有经费项目可以报销');
    }
    if (entryData.excludedFlg) {
      throw new Error('除外记录不能报销');
    }
    await updateDoc(doc(this.firestore, `companies/${this.companyId}/entries`, entryId), {
      refundFlg: true,
      refundAt: new Date(),
      updatedAt: new Date(),
    });
  }

  async deleteEntry(entryId: string) {
    const key = `companies/${this.companyId}/entries/${entryId}`;
    const entrySnap = await getDoc(doc(this.firestore, key));
    if (!entrySnap.exists() || entrySnap.data().status != 'pending') {
      throw new Error('删除失败! 只能删除待承认的记录');
    }
    const date = entrySnap.data()['date'];
    await deleteDoc(doc(this.firestore, key));
    try {
      const folderRef = ref(this.storage, `receipts/${this.companyId}/${date}/${entryId}`);
      const listResult = await listAll(folderRef);
      await Promise.all(listResult.items.map(item => deleteObject(item)));
    } catch {
      // 无图片时忽略
    }
  }

  /**
   * 更新记账记录（仅限待承认状态）
   */
  async updateEntry(entryId: string, data: Partial<{
    type: string;
    amount: number;
    category: string;
    details: string;
    date: string;
    target: string;
    payer: string;
    isFund: boolean;
    rawData: string;
  }>) {
    const entryRef = doc(this.firestore, `companies/${this.companyId}/entries/${entryId}`);
    const entrySnap = await getDoc(entryRef);
    if (!entrySnap.exists() || entrySnap.data().status != 'pending') {
      throw new Error('只能编辑待承认的记录');
    }
    const uid = this.auth.currentUser?.displayName;
    if (!uid) throw new Error('未登录');
    const updateData: any = { ...data, updatedAt: new Date(), updatedBy: uid };
    await updateDoc(entryRef, updateData);
  }

  async getEntries(param: { status?: string, createdBy?: string, dateFrom?: string, dateTo?: string }) {
    const entriesKey = `companies/${this.companyId}/entries`;

    let q = query(
      collection(this.firestore, entriesKey),
    );
    // 动态添加 where 条件  
    if (param.status) {
      if (param.status === 'not_refunded' || param.status === 'submission_0') {
        q = query(q, where('status', '==', 'approved'));
      } else if (!param.status.includes('submission')) {
        q = query(q, where('status', '==', param.status));
      }
    }
    if (param.createdBy) {
      q = query(q, where('createdBy', '==', param.createdBy));
    }
    if (param.dateFrom) {
      q = query(q, where('date', '>=', param.dateFrom));
    }
    if (param.dateTo) {
      q = query(q, where('date', '<=', param.dateTo));
    }

    q = query(q, orderBy('date', "desc"));

    return getDocs(q);
  }


  async getBalanceHistory() {
    const key = `companies/${this.companyId}/summary/balance/history`;
    let q = query(
      collection(this.firestore, key),
    );
    q = query(q, orderBy('createdAt', "desc"));
    return getDocs(q);
  }


  // 创建用户
  async createUser(userData: UserData): Promise<any> {
    try {
      // 在Firebase Authentication中创建用户
      const credential = await createUserWithEmailAndPassword(this.auth,
        userData.email,
        userData.password!
      );

      if (credential.user) {
        // 更新显示名
        await updateProfile(credential.user, {
          displayName: userData.displayName,
          photoURL: null // 可选：设置头像 URL
        });

        // 在Firestore中保存用户信息
        const userDoc = {
          id: credential.user.uid,
          email: userData.email,
          displayName: userData.displayName,
          permissions: userData.permissions,
          mustChangePassword: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const userRef = doc(this.firestore, `companies/${this.companyId}/users/${credential.user.uid}`);
        await setDoc(userRef, userDoc);

        return { success: true, user: userDoc };
      }
    } catch (error) {
      console.error('创建用户失败:', error);
      throw error;
    }
  }

  getUsers() {
    const key = `companies/${this.companyId}/users`;
    const ref = collection(this.firestore, key);
    return getDocs(ref);
  }

  async getCategories() {
    const key = `companies/${this.companyId}/setting/category`;
    const categoriesSnap = await getDoc(doc(this.firestore, key));
    let categories = categoriesSnap.data();

    if (!categories) {
      categories = {
        expense: ['餐饮', '交通', '购物', '娱乐', '医疗', '教育', '房租', '水电费', '其他'],
        income: ['工资', '奖金', '投资', '兼职', '礼金', '退款', '其他']
      };
    }
    return categories;
  }


  // 更新用户权限
  async updateUser(userId: string, permissions: UserPermissions, displayName: string, password: string): Promise<void> {
    await updateDoc(doc(this.firestore, `companies/${this.companyId}/users`, userId), {
      permissions: permissions,
      displayName: displayName,
      updatedAt: new Date()
    });
    if (password) {
      const updateFn = httpsCallable(this.functions, 'updatePasswordByUid');
      await updateFn({ companyId: this.companyId, uid: userId, password: password });
      await updateDoc(doc(this.firestore, `companies/${this.companyId}/users`, userId), {
        mustChangePassword: true,
      });
    }
  }

  // 删除用户
  async deleteUser(userId: string): Promise<void> {
    const deleteFn = httpsCallable(this.functions, 'deleteUserByUid');
    await deleteFn({ companyId: this.companyId, uid: userId });
  }

  // 识别图片
  async recognizeReceipt(base64Image: string): Promise<any> {
    const recognizeFn = httpsCallable(this.functions, 'recognizeReceipt');
    const categories = await this.getCategories();
    const expenseList = categories.expense.join(',');
    
    const promptTemplate = `以下是某张购物小票或英文请求书的文字内容。请你从中提取以下字段：
- 日期 (date)
- 商店名或单位名称 (merchant)
- 总金额 (total)
- 商品或费用列表（如有）(items)
- 对象（如 "张三", "项目 A"）(target)
- 消费类型 (category), 尽量从这些值中选取[${expenseList}],没有合适的可以自定义
- 总税额 (tax)
返回格式为 JSON，字段为：date, merchant, total, items[], target, category, tax。没有的字段留空。
日期格式为 "yyyy-MM-dd"
设定的值,最好使用日语
请**不要**返回任何多余的文本、代码块或解释,返回纯粹的json字符串`;

    return await recognizeFn({ imageBase64: base64Image, promptTemplate: promptTemplate });
  }


  async getPendingCount() {

    const q = query(
      collection(this.firestore, `companies/${this.companyId}/entries`),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);

    return snapshot.size;
  }

  async getPeriodSummary(range: { start: Date | null; end: Date | null }) {

    let q = query(
      collection(this.firestore, `companies/${this.companyId}/entries`),
      where('status', '==', 'approved')
    );
    if (range.start) {
      q = query(q, where('date', '>=', this.hpService.formatDate(range.start)));
    }
    if (range.end) {
      q = query(q, where('date', '<=', this.hpService.formatDate(range.end)));
    }

    const snapshot = await getDocs(q);
    let income = 0;
    let expense = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.excludedFlg) return;   // 已撤销的账目不计入期间汇总，与 getBalance 口径一致
      const amount = Number(data.amount) || 0;
      if (data.type == 'income') {
        income += amount;
      } else {
        expense += amount;
      }
    });

    return { income, expense };
  }

  async getPeriodData(range: { start: Date | null; end: Date | null }) {

    let q = query(
      collection(this.firestore, `companies/${this.companyId}/entries`),
      where('status', '==', 'approved')
    );
    if (range.start) {
      q = query(q, where('date', '>=', this.hpService.formatDate(range.start)));
    }
    if (range.end) {
      q = query(q, where('date', '<=', this.hpService.formatDate(range.end)));
    }

    const snapshot = await getDocs(q);
    let income = 0;
    let expense = 0;

    const summaryMap: Record<string, DaySummary> = {};

    const incomeMap = new Map<string, number>();
    const expenseMap = new Map<string, number>();

    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.excludedFlg) return;   // 已撤销的账目不计入期间汇总，与 getBalance 口径一致
      const date = data.date;
      const category = data.category;
      const amt = Number(data.amount);

      if (!summaryMap[date]) {
        summaryMap[date] = { income: 0, expense: 0 };
      }

      if (data.type == 'income') {
        summaryMap[date].income += amt;
        incomeMap.set(category, (incomeMap.get(category) || 0) + amt);
      } else {
        summaryMap[date].expense += amt;
        expenseMap.set(category, (expenseMap.get(category) || 0) + amt);
      }
    });

    // 把结果转换成数组（按日期升序排列）
    const result = Object.entries(summaryMap)
    .map(([date, values]) => ({
      date,
      ...values
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

    const incomeData: CategorySummary[] = Array.from(incomeMap.entries()).map(([category, amount]) => ({ category, amount }));
    const expenseData: CategorySummary[] = Array.from(expenseMap.entries()).map(([category, amount]) => ({ category, amount }));
    
    return {result, incomeData, expenseData};
  }


  async getBalance() {
    const key = `companies/${this.companyId}/summary/balance`;
    const balanceSnap = await getDoc(doc(this.firestore, key));
    return balanceSnap.exists() ? balanceSnap.data().total : 0;
  }

  async uploadReceipts(files: File[], entryId: string, entryDate: string) {
    const urls: string[] = [];
    for (const file of files) {
      const storageRef = ref(this.storage, `receipts/${this.companyId}/${entryDate}/${entryId}/${file.name}`);
      await uploadBytes(storageRef, file);
      urls.push(await getDownloadURL(storageRef));
    }
    await updateDoc(doc(this.firestore, `companies/${this.companyId}/entries`, entryId), {
      receiptUrls: urls,
    });
  }

  /**
   * 编辑账目时补传小票：追加到该账目已有的 receiptUrls（兼容旧的单数 receiptUrl 字段）后写回，
   * 不会像 uploadReceipts 那样整字段覆盖，避免把已有小票从 Storage 引用中抹掉。
   * 不做任何 OCR 识别调用。
   * 文件名加时间戳+序号前缀唯一化，避免补传文件与该账目已有小票同名时
   * uploadBytes 覆盖写把 Storage 里的原图整个抹掉（例如 iOS 拍照文件名长年是 image.jpg）。
   *
   * **逐张配对：每传完一张立刻落库（B-015）。**
   * 改前是「全部文件传完，最后一次性写文档」。中途任何一步失败（网络断开、规则拒绝、
   * 页面被关掉）都会让已经传上去的文件**永远不被 receiptUrls 引用**——界面上看不见，
   * 除 deleteEntry 外没有任何逻辑会清理它，成为持续计费的 Storage 孤儿。
   * 改为逐张配对后，任一时刻「已上传的文件」与「文档已引用的 URL」最多相差一张
   * （即当前正在处理的那张），失败时前面成功的都已经挂在账目上、用户看得见。
   *
   * 注意：字面意义的「先写文档、后传文件」做不到——receiptUrls 存的是 getDownloadURL()
   * 返回的带 token 下载地址，文件上传完成之前它并不存在。逐张配对是能达成同一目的的最近做法。
   */
  async appendReceipts(files: File[], entryId: string, entryDate: string) {
    const entryRef = doc(this.firestore, `companies/${this.companyId}/entries`, entryId);
    const uid = this.auth.currentUser?.displayName;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uniqueName = `${Date.now()}_${i}_${file.name}`;
      const storageRef = ref(this.storage, `receipts/${this.companyId}/${entryDate}/${entryId}/${uniqueName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      // 每轮都重新读一次文档：合并基准必须是最新的 receiptUrls，
      // 否则第 2 张会拿「第 1 张写入之前」的旧值去 merge，把第 1 张挤掉。
      const entrySnap = await getDoc(entryRef);
      const mergedUrls = mergeReceiptUrls(entrySnap.exists() ? (entrySnap.data() as any) : null, [url]);
      await updateDoc(entryRef, {
        receiptUrls: mergedUrls,
        updatedAt: new Date(),
        updatedBy: uid,
      });
    }
  }

  setCompanyId(companyId: string) {
    this.companyId = companyId;
  }

}
